import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Spacing, Input, Button } from '@reservamos/elements';
import SectionTitle from 'ui/atoms/SectionTitle';
import ExchangeInfoModal from 'ui/molecules/ExchangeInfoModal';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';
import SNDisclaimer from './SNDisclaimer';

/**
 * The form's field stack: a plain column under the redesign, where each field's own top margin
 * is the spacing, and the tenant's usual `Spacing` row everywhere else.
 *
 * @param {object} props - Component props.
 * @param {boolean} props.isStacked - Whether the redesign's single column is in use.
 * @param {React.ReactNode} props.children - The fields.
 * @returns {JSX.Element}
 */
const FieldStack = ({ isStacked, children, ...spacingProps }) =>
  isStacked ? (
    <div className="flex w-full flex-col">{children}</div>
  ) : (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Spacing {...spacingProps}>{children}</Spacing>
  );

FieldStack.propTypes = {
  isStacked: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * SNForm Functional Component
 *
 * @param {Object} props - The component props.
 * @param {Function} props.handleSubmit - The function to be called when the form is submitted.
 * @param {string} props.ctaText - The text to be displayed on the call-to-action button.
 * @param {Function} props.handleClickButton - Function to execute when clicking the submit button.
 * @param {boolean} [props.hideTitle] - Whether to leave the title out, when the line selector
 *   above the form already titles the step.
 *
 * @returns {JSX.Element} The rendered form component.
 */
const SNForm = ({ handleSubmit, ctaText, handleClickButton, hideTitle = false }) => {
  const { t } = useTranslation('exchange');
  const features = useWhitelabelFeatures();
  const useNewExchangeUi = Boolean(features.USE_NEW_EXCHANGE_UI);
  // The redesign gives every field the same `XL` radius the rest of the exchange forms use.
  const fieldRadius = useNewExchangeUi ? 'XL' : undefined;
  const initialValues = {
    operationNumber: '',
    document: '',
    email: '',
  };

  const validationSchema = yup.object().shape({
    operationNumber: yup.string().required(''),
    document: yup.string().required(''),
    email: yup.string().required(''),
  });

  /**
   * The function `getSectionTitleContent` determines the title and description content based on the
   * values of `features.SHOW_EXCHANGE_TICKET_BUTTON` and `features.SHOW_CANCEL_TICKET_BUTTON`.
   * @returns The function `getSectionTitleContent` returns an object with a `title` and `description`
   * based on the conditions of the `features` object. The returned object will have a `title` and
   * `description` based on whether `SHOW_EXCHANGE_TICKET_BUTTON` and `SHOW_CANCEL_TICKET_BUTTON` are
   * true or false.
   */
  const getSectionTitleContent = () => {
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.exchange_cancelations'),
        description: t('subtitle.exchange_cancelations'),
      };
    }
    if (!features.SHOW_EXCHANGE_TICKET_BUTTON && features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.cancelations'),
        description: t('subtitle.cancelations'),
      };
    }
    if (features.SHOW_EXCHANGE_TICKET_BUTTON && !features.SHOW_CANCEL_TICKET_BUTTON) {
      return {
        title: t('title.exchange'),
        description: t('subtitle.exchange'),
      };
    }
    return {
      title: t('title.exchange_cancelations'),
      description: t('subtitle.exchange_cancelations'),
    };
  };

  const sectionTitleContent = getSectionTitleContent();

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) =>
        handleSubmit({
          operationNumbers: [values.operationNumber],
          document: values.document,
          email: values.email,
        })
      }
    >
      {({ values, handleChange, handleBlur, errors, touched }) => (
        <Form>
          <Spacing vertical responsiveSize="XL" size={useNewExchangeUi ? 'M' : undefined}>
            {!hideTitle && (
              <SectionTitle
                title={sectionTitleContent.title}
                description={sectionTitleContent.description}
              />
            )}
            <ul className="list-disc list-inside">
              <li className="text-base">{t('labels.ticket_number')}</li>
              <li className="text-base">{t('labels.passenger_document')}</li>
              <li className="text-base">{t('labels.email')}</li>
            </ul>
            {/* The redesign stacks the fields: the shell gives the form a narrow column, and
                three fields sharing a row left each one too small to read its own label. The
                stack adds no spacing of its own — each field already carries the gap the rest of
                the column sits on. */}
            <FieldStack isStacked={useNewExchangeUi} flexGrow isResponsive responsiveSize="S">
              <Input
                autocomplete="off"
                id="operationNumber"
                name="operationNumber"
                label={t('labels.ticket_number')}
                maxLength={15}
                errorMessage={errors.operationNumber}
                hasError={errors.operationNumber !== undefined && touched.operationNumber}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.operationNumber}
                borderRadius={fieldRadius}
              />
              <Input
                id="document"
                name="document"
                label={t('labels.passenger_document')}
                maxLength={15}
                errorMessage={errors.document}
                hasError={errors.document !== undefined && touched.document}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.document}
                borderRadius={fieldRadius}
              />
              <Input
                id="email"
                name="email"
                type="email"
                label={t('labels.email')}
                maxLength={60}
                errorMessage={errors.email}
                hasError={errors.email !== undefined && touched.email}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.email}
                borderRadius={fieldRadius}
              />
            </FieldStack>
            {/* Kept for every tenant: where to find the ticket number is the question this form
                asks first, and the shell's panel does not answer it for this family. */}
            <ExchangeInfoModal />
            {useNewExchangeUi ? (
              <Button
                buttonType="submit"
                text={ctaText}
                variant="accent"
                isRounded
                withHeight
                fullWidth
                onClick={handleClickButton}
              />
            ) : (
              <Spacing justifyContent="space-between" isResponsive>
                {/* On the redesigned shell these rules are rendered below the form instead, in
                    the footer that every brand shares. Here the block holds its place even when
                    the tenant has no rules, which is what this row did before the extraction. */}
                <SNDisclaimer collapseWhenEmpty={false} />
                <Spacing isResponsive responsiveColumnReverse>
                  <Button
                    buttonType="submit"
                    text={ctaText}
                    variant="accent"
                    isRounded
                    withHeight
                    fullWidthOnSmall
                    onClick={handleClickButton}
                  />
                </Spacing>
              </Spacing>
            )}
          </Spacing>
        </Form>
      )}
    </Formik>
  );
};

const propTypes = {
  handleSubmit: PropTypes.func,
  ctaText: PropTypes.string,
  handleClickButton: PropTypes.func,
  hideTitle: PropTypes.bool,
};

SNForm.propTypes = propTypes;

export default SNForm;
